/**
 * Library List Page
 * 
 * Phase C2 - Display documents in a table view
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Button, Table, EmptyState, Badge } from '@threadlock/ui';
import type { TableColumn } from '@threadlock/ui';
import { ROUTES } from '@threadlock/routing';
import { listDocuments, type RbDocument } from '@threadlock/data';
import { db } from '../../firebase-init';
import { useWorkspaceState } from '../../config/workspace';

function getStatusColor(status: string): 'default' | 'success' | 'warning' | 'error' {
  switch (status) {
    case 'ready':
      return 'success';
    case 'processing':
    case 'uploaded':
      return 'warning';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

function formatDate(timestamp: { toDate: () => Date }): string {
  return timestamp.toDate().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default function LibraryListPage() {
  const navigate = useNavigate();
  const { activeWorkspaceId } = useWorkspaceState();
  const [documents, setDocuments] = useState<RbDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadDocuments() {
      if (!activeWorkspaceId) {
        setLoading(false);
        return;
      }

      try {
        const docs = await listDocuments(db, activeWorkspaceId);
        setDocuments(docs);
      } catch (err) {
        console.error('Failed to load documents:', err);
        setError('Failed to load documents. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    loadDocuments();
  }, [activeWorkspaceId]);

  const columns: TableColumn<RbDocument>[] = [
    {
      key: 'filename',
      label: 'Filename',
      render: (doc) => (
        <button
          onClick={() => navigate(ROUTES.LIBRARY_DOCUMENT.replace(':documentId', doc.id))}
          className="text-left hover:underline text-[var(--brand-primary)] font-medium"
        >
          {doc.filename}
        </button>
      ),
      width: '30%',
    },
    {
      key: 'documentType',
      label: 'Document Type',
      render: (doc) => doc.documentType,
    },
    {
      key: 'jurisdiction',
      label: 'Jurisdiction',
      render: (doc) => doc.jurisdiction,
    },
    {
      key: 'policyTier',
      label: 'Policy Tier',
      render: (doc) => doc.policyTier,
    },
    {
      key: 'status',
      label: 'Status',
      render: (doc) => (
        <Badge variant={getStatusColor(doc.status)}>
          {doc.status}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (doc) => formatDate(doc.createdAt),
    },
  ];

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Library"
          description="Manage your knowledge base documents"
        />
        <div className="mt-8 text-center text-[var(--surface-text-muted)]">
          Loading documents...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto">
        <PageHeader
          title="Library"
          description="Manage your knowledge base documents"
        />
        <div className="mt-8 text-center text-[var(--status-error)]">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <PageHeader
        title="Library"
        description="Manage your knowledge base documents"
        actions={
          <Button onClick={() => navigate(ROUTES.LIBRARY_UPLOAD)}>
            Upload Document
          </Button>
        }
      />

      <div className="mt-8">
        {documents.length === 0 ? (
          <EmptyState
            title="No documents yet"
            description="Upload your first document to get started with your knowledge base."
            action={
              <Button onClick={() => navigate(ROUTES.LIBRARY_UPLOAD)}>
                Upload Document
              </Button>
            }
          />
        ) : (
          <div className="bg-[var(--surface-bg-panel)] border border-[var(--surface-border-subtle)] rounded-[var(--radius-xl)] overflow-hidden">
            <Table
              columns={columns}
              data={documents}
              keyExtractor={(doc) => doc.id}
              emptyMessage="No documents found"
            />
          </div>
        )}
      </div>
    </div>
  );
}
